import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveBrandTicker } from '../siteBrandTicker';
import { fetchSite, getToken, patchBrandTicker, uploadFile } from './api';
import { AdminImagePreview, AdminModal, fileInput, input, label } from './adminUi';

function emptyAgriFeature() {
  return { step: '', title: '', text: '', ctaLabel: 'More Details', ctaHref: '#contact' };
}

function normalizeLoadedFeatures(rawList) {
  const list = Array.isArray(rawList) ? rawList : [];
  return [0, 1].map((i) => {
    const x = list[i];
    const b = emptyAgriFeature();
    if (!x || typeof x !== 'object') return b;
    return {
      step: typeof x.step === 'string' ? x.step : b.step,
      title: typeof x.title === 'string' ? x.title : b.title,
      text: typeof x.text === 'string' ? x.text : b.text,
      ctaLabel:
        typeof x.ctaLabel === 'string' && x.ctaLabel.trim() ? x.ctaLabel : b.ctaLabel,
      ctaHref: typeof x.ctaHref === 'string' && x.ctaHref.trim() ? x.ctaHref : b.ctaHref,
    };
  });
}

function loadAgriFromSite(site) {
  const ai = site?.agriIntelligence && typeof site.agriIntelligence === 'object' ? site.agriIntelligence : {};
  return {
    sectionLabel: typeof ai.sectionLabel === 'string' ? ai.sectionLabel : '',
    sectionTitle: typeof ai.sectionTitle === 'string' ? ai.sectionTitle : '',
    centerImageSrc: typeof ai.centerImageSrc === 'string' ? ai.centerImageSrc : '',
    centerImageAlt: typeof ai.centerImageAlt === 'string' ? ai.centerImageAlt : '',
    features: normalizeLoadedFeatures(ai.features),
  };
}

const cardClass = 'rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5';
const primaryBtnClass =
  'rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:opacity-95 active:opacity-90 disabled:opacity-50';
const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';
const ghostBtnClass =
  'rounded-lg px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100';

function ModalFooter({ onCancel, onApply }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>
      <button type="button" onClick={onApply} className={primaryBtnClass}>
        Apply
      </button>
    </div>
  );
}

function clip(s, n) {
  if (typeof s !== 'string' || !s.trim()) return '—';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

const FEATURE_TITLES = ['Feature 1 (left column)', 'Feature 2 (right column)'];

export default function BrandTickerDashboard() {
  const navigate = useNavigate();
  const [lines, setLines] = useState('');
  const [sectionLabel, setSectionLabel] = useState('');
  const [sectionTitle, setSectionTitle] = useState('');
  const [centerImageSrc, setCenterImageSrc] = useState('');
  const [centerImageAlt, setCenterImageAlt] = useState('');
  const [features, setFeatures] = useState([emptyAgriFeature(), emptyAgriFeature()]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingCenter, setUploadingCenter] = useState(false);

  const [modalErr, setModalErr] = useState('');
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [sectionDraft, setSectionDraft] = useState(null);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [featureModalIndex, setFeatureModalIndex] = useState(null);
  const [featureDraft, setFeatureDraft] = useState(null);
  const [tickerModalOpen, setTickerModalOpen] = useState(false);
  const [linesDraft, setLinesDraft] = useState('');

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => {
        setLines(resolveBrandTicker(site).join('\n'));
        const a = loadAgriFromSite(site);
        setSectionLabel(a.sectionLabel);
        setSectionTitle(a.sectionTitle);
        setCenterImageSrc(a.centerImageSrc);
        setCenterImageAlt(a.centerImageAlt);
        setFeatures(a.features);
      })
      .catch(() => {
        setLines(resolveBrandTicker(DEFAULT_SITE).join('\n'));
        const a = loadAgriFromSite(DEFAULT_SITE);
        setSectionLabel(a.sectionLabel);
        setSectionTitle(a.sectionTitle);
        setCenterImageSrc(a.centerImageSrc);
        setCenterImageAlt(a.centerImageAlt);
        setFeatures(a.features);
      });
  }, [navigate]);

  function closeSectionModal() {
    setSectionModalOpen(false);
    setSectionDraft(null);
    setModalErr('');
  }

  function openSectionModal() {
    setModalErr('');
    setSectionDraft({
      sectionLabel,
      sectionTitle,
      centerImageSrc,
      centerImageAlt,
    });
    setSectionModalOpen(true);
  }

  function applySectionModal() {
    if (!sectionDraft) return;
    setSectionLabel(sectionDraft.sectionLabel);
    setSectionTitle(sectionDraft.sectionTitle);
    setCenterImageSrc(sectionDraft.centerImageSrc);
    setCenterImageAlt(sectionDraft.centerImageAlt);
    closeSectionModal();
  }

  async function onCenterDraftUpload(file) {
    if (!file) return;
    setErr('');
    setMsg('');
    setUploadingCenter(true);
    try {
      const { url } = await uploadFile(file);
      setModalErr('');
      setSectionDraft((d) => (d ? { ...d, centerImageSrc: url } : d));
      setMsg('Image uploaded to Cloudinary. Click Save to publish.');
    } catch (e) {
      const m = e.message || 'Upload failed';
      setErr(m);
      setModalErr(m);
    } finally {
      setUploadingCenter(false);
    }
  }

  function closeFeatureModal() {
    setFeatureModalOpen(false);
    setFeatureModalIndex(null);
    setFeatureDraft(null);
    setModalErr('');
  }

  function openFeatureModal(index) {
    const f = features[index];
    if (!f) return;
    setModalErr('');
    setFeatureDraft({ ...f });
    setFeatureModalIndex(index);
    setFeatureModalOpen(true);
  }

  function applyFeatureModal() {
    if (featureDraft == null || featureModalIndex == null) return;
    setFeatures((prev) => {
      const next = [...prev];
      next[featureModalIndex] = { ...featureDraft };
      return next;
    });
    closeFeatureModal();
  }

  function closeTickerModal() {
    setTickerModalOpen(false);
    setLinesDraft('');
    setModalErr('');
  }

  function openTickerModal() {
    setModalErr('');
    setLinesDraft(lines);
    setTickerModalOpen(true);
  }

  function applyTickerModal() {
    setLines(linesDraft);
    closeTickerModal();
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const names = lines
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const { brandTicker, agriIntelligence } = await patchBrandTicker({
        names,
        sectionLabel,
        sectionTitle,
        centerImageSrc,
        centerImageAlt,
        features,
      });
      setLines(resolveBrandTicker({ brandTicker: brandTicker || names }).join('\n'));
      if (agriIntelligence && typeof agriIntelligence === 'object') {
        const a = loadAgriFromSite({ agriIntelligence });
        setSectionLabel(a.sectionLabel);
        setSectionTitle(a.sectionTitle);
        setCenterImageSrc(a.centerImageSrc);
        setCenterImageAlt(a.centerImageAlt);
        setFeatures(a.features);
      }
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const nameCount = lines
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean).length;

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <h1 className="text-lg font-bold text-neutral-900">Agri Intelligence</h1>
          <p className="text-sm text-neutral-600">Section, center graphic, features, and scrolling brand names — edit in dialogs</p>
        </div>
        <button type="button" disabled={saving} onClick={save} className={primaryBtnClass}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {msg}
          </p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="space-y-6">
          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-neutral-500">{clip(sectionLabel, 40)}</p>
                <h2 className="mt-1 text-lg font-bold text-neutral-900">{clip(sectionTitle, 56)}</h2>
                {centerImageSrc ? (
                  <div className="mt-3 flex max-w-[200px] items-center gap-2">
                    <img
                      src={centerImageSrc}
                      alt={centerImageAlt || ''}
                      className="h-16 w-16 shrink-0 rounded-lg object-cover ring-1 ring-neutral-200"
                    />
                    <p className="m-0 text-xs text-neutral-500">Center graphic set</p>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-neutral-500">No center graphic yet.</p>
                )}
              </div>
              <button type="button" onClick={openSectionModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
          </section>

          <section className={cardClass}>
            <h2 className="m-0 text-base font-bold text-neutral-900">Feature columns</h2>
            <p className="mt-1 text-xs text-neutral-500">Two side columns beside the center graphic</p>
            <ul className="mt-4 list-none space-y-2 p-0">
              {FEATURE_TITLES.map((title, fi) => {
                const f = features[fi] || emptyAgriFeature();
                return (
                  <li
                    key={title}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-xs font-semibold text-neutral-500">{title}</p>
                      <p className="mt-0.5 text-sm font-semibold text-neutral-900">
                        {clip(f.step, 8)} {clip(f.title, 44)}
                      </p>
                      <p className="mt-0.5 text-xs text-neutral-600">{clip(f.text, 72)}</p>
                    </div>
                    <button type="button" className={ghostBtnClass} onClick={() => openFeatureModal(fi)}>
                      Edit
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-base font-bold text-neutral-900">Scrolling brand names</h2>
                <p className="mt-1 text-sm text-neutral-600">
                  {nameCount} name{nameCount === 1 ? '' : 's'} · {clip(lines.split('\n').find((l) => l.trim()) || '', 48)}
                </p>
              </div>
              <button type="button" onClick={openTickerModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
          </section>
        </div>
      </div>

      <AdminModal
        open={sectionModalOpen}
        title="Section & center graphic"
        description="Eyebrow, headline, center image URL, alt text, and upload."
        error={modalErr}
        onClose={closeSectionModal}
        footer={<ModalFooter onCancel={closeSectionModal} onApply={applySectionModal} />}
      >
        {sectionDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={`${label} md:col-span-2`}>
              Section label (small line above headline)
              <input
                className={input}
                value={sectionDraft.sectionLabel}
                onChange={(e) => {
                  setModalErr('');
                  setSectionDraft((d) => ({ ...d, sectionLabel: e.target.value }));
                }}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Section headline
              <input
                className={input}
                value={sectionDraft.sectionTitle}
                onChange={(e) => {
                  setModalErr('');
                  setSectionDraft((d) => ({ ...d, sectionTitle: e.target.value }));
                }}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Image URL (Cloudinary or HTTPS)
              <input
                className={`${input} font-mono text-sm`}
                value={sectionDraft.centerImageSrc}
                onChange={(e) => {
                  setModalErr('');
                  setSectionDraft((d) => ({ ...d, centerImageSrc: e.target.value }));
                }}
                placeholder="https://…"
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Upload image
              <input
                type="file"
                accept="image/*"
                disabled={uploadingCenter}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  onCenterDraftUpload(f);
                }}
              />
            </label>
            {uploadingCenter ? <p className="text-xs text-neutral-500 md:col-span-2">Uploading…</p> : null}
            <label className={`${label} md:col-span-2`}>
              Alt text (accessibility)
              <input
                className={input}
                value={sectionDraft.centerImageAlt}
                onChange={(e) => setSectionDraft((d) => ({ ...d, centerImageAlt: e.target.value }))}
              />
            </label>
            <div className="md:col-span-2">
              <AdminImagePreview src={sectionDraft.centerImageSrc} alt={sectionDraft.centerImageAlt || ''} />
            </div>
            {sectionDraft.centerImageSrc ? (
              <div className="md:col-span-2">
                <button
                  type="button"
                  onClick={() => setSectionDraft((d) => (d ? { ...d, centerImageSrc: '' } : d))}
                  className="text-xs font-medium text-neutral-600 underline decoration-neutral-300 hover:text-neutral-900"
                >
                  Clear image URL
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={featureModalOpen}
        title={featureModalIndex != null ? `Edit ${FEATURE_TITLES[featureModalIndex]}` : 'Edit feature'}
        description="Step label, title, body, and optional CTA for this column."
        error={modalErr}
        onClose={closeFeatureModal}
        footer={<ModalFooter onCancel={closeFeatureModal} onApply={applyFeatureModal} />}
      >
        {featureDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Step / number
              <input
                className={input}
                value={featureDraft.step}
                onChange={(e) => setFeatureDraft((d) => ({ ...d, step: e.target.value }))}
              />
            </label>
            <label className={label}>
              Title
              <input
                className={input}
                value={featureDraft.title}
                onChange={(e) => setFeatureDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Description
              <textarea
                className={`${input} min-h-[100px] resize-y text-sm`}
                value={featureDraft.text}
                onChange={(e) => setFeatureDraft((d) => ({ ...d, text: e.target.value }))}
              />
            </label>
            <label className={label}>
              Button label
              <input
                className={input}
                value={featureDraft.ctaLabel}
                onChange={(e) => setFeatureDraft((d) => ({ ...d, ctaLabel: e.target.value }))}
              />
            </label>
            <label className={label}>
              Button link (URL or #anchor)
              <input
                className={`${input} font-mono text-sm`}
                value={featureDraft.ctaHref}
                onChange={(e) => setFeatureDraft((d) => ({ ...d, ctaHref: e.target.value }))}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={tickerModalOpen}
        title="Scrolling brand names"
        description="One brand name per line. Blank lines are ignored when you save."
        error={modalErr}
        onClose={closeTickerModal}
        footer={<ModalFooter onCancel={closeTickerModal} onApply={applyTickerModal} />}
      >
        <label className={label}>
          Brand names (one per line)
          <textarea
            className={`${input} min-h-[220px] resize-y font-mono text-sm`}
            value={linesDraft}
            onChange={(e) => {
              setModalErr('');
              setLinesDraft(e.target.value);
            }}
          />
        </label>
      </AdminModal>
    </>
  );
}
